
import React, { useCallback, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout
} from '@stripe/react-stripe-js';
import { fallbackRestUrl, stripe_public_key, stripe_single_story_price_id } from '@/utils';
import { useSelector } from 'react-redux';
import { authSelector, subscriptionSelector } from '@/store/selectors';
import { Box, Typography } from '@mui/material';

const stripePromise = loadStripe(stripe_public_key);
const API_URL = process.env.REST_URL || fallbackRestUrl;

export default function PaymentForm() {
    const { user } = useSelector(authSelector);
  const { selectedTier } = useSelector(subscriptionSelector);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);

  // Check if this is a single story purchase
  const isSingleStoryPurchase = selectedTier?.Tier?.isSinglePurchase === true;

  const fetchClientSecret = useCallback(() => {
    // Use different endpoint and payload for single story purchase
    const endpoint = isSingleStoryPurchase
      ? `${API_URL}/subscription/create-single-story-purchase-session`
      : `${API_URL}/subscription/create-checkout-session`;

    const body = isSingleStoryPurchase
      ? {
          priceId: stripe_single_story_price_id,
          userEmail: user?.email,
          userId: user?.id,
        }
      : {
          priceId: selectedTier?.Tier?.stripePriceId,
          userEmail: user?.email
        };

    return fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${user?.token}`,
      },
      body: JSON.stringify(body),
    })
      .then(async (res) => {
        if (!res.ok) {
          const errorData = await res.json().catch(() => null);
          let message = errorData?.error || errorData?.message || "Something went wrong. Please try again.";
          message = message.replace(/^Failed to create (?:checkout|single story purchase) session:\s*/i, '');
          setCheckoutError(message);
          throw new Error(message);
        }
        return res.json();
      })
      .then((data) => data?.result?.clientSecret);
  }, [isSingleStoryPurchase, selectedTier, user]);
  const options = {fetchClientSecret};

  if (checkoutError) {
    return (
      <Box id="checkout" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: '300px', p: 4 }}>
        <Box sx={{ backgroundColor: '#fff', borderRadius: '12px', p: 4, maxWidth: '450px', textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.1)' }}>
          <Typography sx={{ fontSize: '1.25rem', fontWeight: 600, mb: 1, color: '#1a1a2e' }}>
            Unable to proceed
          </Typography>
          <Typography sx={{ fontSize: '1rem', color: '#555' }}>
            {checkoutError}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box id="checkout">
      <EmbeddedCheckoutProvider
        stripe={stripePromise}

        options={options}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </Box>
  )
}
